import React, { ReactElement, ForwardedRef } from 'react';
import TextField from '@mui/material/TextField';
import ActionButton from '../action-button/action-button.component';
import RequiredMark from '../required-mark/required-mark.component';
import TextBlock from '../text-block/text-block.component';
import Tooltip from '../tooltip/tooltip.component';
import PasswordStrengthBar from '../password-strength-bar/password-strength-bar.component';
import VisibilityOnIcon from '../../icons/visibility-on-icon';
import VisibilityOffIcon from '../../icons/visibility-off-icon';
import FormatService from '../../services/format-service';
import useMobileDevice from '../../hooks/use-mobile-device';
import useStylesFieldPassword from './field-password.styles';
import { TAB_INDEX_NONE } from '../../constants/system/tab-indexes';

const FieldPassword: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const INPUT_TYPE_TEXT: string = 'text';
  const INPUT_TYPE_PASSWORD: string = 'password';
  const VARIANT_OUTLINED: TextFieldVariant = 'outlined';
  const AUTOCOMPLETE_ON: string = 'on';
  const AUTOCOMPLETE_OFF: string = 'off';
  const AUTOCOMPLETE_NEW_PASSWORD: string = 'new-password';
  const AUTOCOMPLETE_CURRENT_PASSWORD: string = 'current-password';

  const {
    isDisabled,
    isOutlined,
    isBordered,
    isVisible,
    isRequired,
    withUnderline,
    withAutocomplete,
    withNoAutocomplete,
    withAutocompleteNewPassword,
    withAutocompleteCurrentPassword,
    withStrengthBar,
    className,
    label,
    placeholder,
    onVisibilityChange,
    input,
    meta,
    ...custom
  } = props;

  const {
    touched: isTouched,
    invalid: isInvalid,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldPassword();
  const isMobileDevice: boolean = useMobileDevice();

  const withVisibilityChange: boolean = !!onVisibilityChange;
  const withErrors: boolean = ((isTouched && isInvalid) || !!formError || !!formSubmitError)
    && !modifiedSinceLastSubmit;

  const outlinedClassName: string | undefined = isOutlined ? classes.fieldPasswordOutlined : undefined;
  const borderedClassName: string | undefined = isBordered ? classes.fieldPasswordBordered : undefined;
  const noUnderlineClassName: string | undefined = !withUnderline ? classes.fieldPasswordNoUnderline : undefined;
  const visibilityChangeableClassName: string | undefined = withVisibilityChange
    ? classes.fieldPasswordVisibilityChangeable : undefined;
  const actionButtonOutlinedClassName: string | undefined = isOutlined
    ? classes.fieldPasswordActionButtonOutlined : undefined;

  const autoCompleteOn: string | undefined = withAutocomplete ? AUTOCOMPLETE_ON : undefined;
  const autoCompleteOff: string | undefined = withNoAutocomplete ? AUTOCOMPLETE_OFF : undefined;
  const autoCompleteNewPassword: string | undefined = withAutocompleteNewPassword
    ? AUTOCOMPLETE_NEW_PASSWORD : undefined;
  const autoCompleteCurrentPassword: string | undefined = withAutocompleteCurrentPassword
    ? AUTOCOMPLETE_CURRENT_PASSWORD : undefined;
  const autoCompleteValue: string | undefined = autoCompleteOn || autoCompleteNewPassword
    || autoCompleteCurrentPassword || autoCompleteOff;

  return (
    <div ref={ref}>
      <TextField
        disabled={isDisabled}
        label={(
          isOutlined ? (
            <TextBlock>
              <TextBlock>
                {label}
              </TextBlock>
              {
                isRequired && (
                  <RequiredMark />
                )
              }
            </TextBlock>
          ) : label
        )}
        placeholder={placeholder || label}
        error={withErrors}
        helperText={withErrors && (formError || formSubmitError)}
        variant={isOutlined ? VARIANT_OUTLINED : undefined}
        type={isVisible ? INPUT_TYPE_TEXT : INPUT_TYPE_PASSWORD}
        className={FormatService.formatClassNames(
          classes.fieldPassword, outlinedClassName, borderedClassName, noUnderlineClassName,
          visibilityChangeableClassName, className,
        )}
        inputProps={{
          maxLength: FormatService.STRING_LIMIT_DEFAULT,
        }}
        {
          ...(autoCompleteValue ? {
            autoComplete: autoCompleteValue,
          } : {})
        }
        {...input}
        {...custom}
      />
      {
        withVisibilityChange && (
          <TextBlock className={classes.fieldPasswordIcon}>
            <Tooltip title={isVisible ? 'Hide Password' : 'Show Password'}>
              <ActionButton
                withNoPadding={!isOutlined}
                withSmallPadding={isOutlined}
                tabIndex={TAB_INDEX_NONE}
                className={FormatService.formatClassNames(
                  classes.fieldPasswordActionButton, actionButtonOutlinedClassName,
                )}
                onClick={(): void => {
                  if (isMobileDevice && onVisibilityChange) {
                    onVisibilityChange(!isVisible);
                  }
                }}
                onMouseDown={(): void => {
                  if (!isMobileDevice && onVisibilityChange) {
                    onVisibilityChange(true);
                  }
                }}
                onMouseUp={(): void => {
                  if (!isMobileDevice && onVisibilityChange) {
                    onVisibilityChange(false);
                  }
                }}
                onMouseLeave={(): void => {
                  if (!isMobileDevice && onVisibilityChange) {
                    onVisibilityChange(false);
                  }
                }}
              >
                {isVisible ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
              </ActionButton>
            </Tooltip>
          </TextBlock>
        )
      }
      {
        (withStrengthBar && !!input.value) && (
          <PasswordStrengthBar passwordValue={input.value} />
        )
      }
    </div>
  );
}));

export default FieldPassword;
